import { Box, Grid, Tooltip } from '@mui/material';
import { useFormik } from 'formik';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { BackButton } from 'src/components/shared/Button/BackButton';
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DescriptionList } from 'src/components/shared/DescriptionList/DescriptionList';
import {
  Divider,
  VerticalDivider
} from 'src/components/shared/Divider/Divider';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { StickyFormHeader } from 'src/components/shared/Forms/StickyFormHeader/StickyFormHeader';
import { Modal } from 'src/components/shared/Modal/Modal';
import { StatusCircle } from 'src/components/shared/StatusCircle/StatusCircle';
import { Header4 } from 'src/components/shared/Typography/Header4';
import { selectActiveGrades } from 'src/store/grades/grades.selectors';
import { selectPackagings } from 'src/store/packagings/packagings.selectors';
import { selectPallets } from 'src/store/pallets/pallets.selectors';
import { ProductDetailDto, ProductStatus } from 'src/store/products/product';
import { selectProductStatuses } from 'src/store/products/products.selectors';
import { selectActiveRunSystems } from 'src/store/runsystems/runsystems.selectors';
import { SpecificationDto } from 'src/store/specifications/specification';
import { formatDate } from 'src/utils/date';
import {
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';
import { LabelsContainer } from './Labels/LabelsContainer';

export const productStatusColorMap = {
  [ProductStatus.InProduction]: 'var(--yellow-status-color-rgb)',
  [ProductStatus.Approved]: 'var(--green-status-color-rgb)',
  [ProductStatus.ApprovedWithDeviation]: 'var(--orange-status-color-rgb)'
};

type Props = {
  product: Nullable<ProductDetailDto>;
  specification: Nullable<SpecificationDto>;
  onCancel: () => void;
  onSubmit: (product: ProductDetailDto) => void;
  onCreateLot: (id: number) => void;
  onReadyForMixture: (id: number) => void;
  onSpecificationChange: () => void;
  onDownloadDeliveryNote: () => void;
  onDownloadProductData: () => void;
};

export const ProductDetailsForm = (props: Props) => {
  const [product, setProduct] = useState<ProductDetailDto>({
    productStatusId: 3
  } as ProductDetailDto);

  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [showLabelsModal, setShowLabelsModal] = useState<boolean>(false);
  const { t } = useTranslation();

  const runSystems = useSelector(selectActiveRunSystems);
  const grades = useSelector(selectActiveGrades);
  const pallets = useSelector(selectPallets);
  const packagings = useSelector(selectPackagings);
  const productStatuses = useSelector(selectProductStatuses);

  const isDisabled =
    !product?.id || product.lotNo === null || props.specification?.lockWaybill;

  const disabledReason = !product?.id
    ? t('disabledReason.noID')
    : product.lotNo === null
    ? t('disabledReason.noLot')
    : props.specification?.lockWaybill
    ? t('disabledReason.noWaybill')
    : '';

  useEffect(() => {
    if (props.product) {
      setProduct(props.product);
    }
  }, [props.product]);

  const validate = (product: ProductDetailDto) => {
    const validationRunners = [
      validationRunner('runSystemId', t('runSystem.singular'), required),
      validationRunner('gradeId', t('grade.singular'), required)
    ];
    return runValidations(product, validationRunners);
  };

  const formik = useFormik({
    initialValues: product,
    enableReinitialize: true,
    validate: validate,
    onSubmit: (values, { setSubmitting }) => {
      props.onSubmit(values);
      setSubmitting(false);
    }
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    submitCount
  } = formik;

  return (
    <Fragment>
      <StickyFormHeader className="flex align-center jc-sb">
        <DescriptionList>
          <BackButton onClick={props.onCancel} />
          <li>
            <Header4>{t('product.runDate')}</Header4>
            {product?.runDate ? formatDate(product.runDate) : '-'}
          </li>
          <li>
            <Header4>{t('product.runNo')}</Header4>
            {product?.runNo || '-'}
          </li>
          <li>
            <Header4>{t('product.lotNo')}</Header4>
            {product?.lotNo || '-'}
          </li>
          <li>
            <Header4>{t('product.norlabNo')}</Header4>
            {product?.norlabNo || '-'}
          </li>
          <li>
            <Header4>{t('props.status')}</Header4>
            <span className="flex align-center jc-sb truncate">
              {product?.productStatus?.name || '-'}
              <StatusCircle
                color={
                  productStatusColorMap[
                    product.productStatusId as ProductStatus
                  ]
                }
                className="ml-16"
              />
            </span>
          </li>
          <li>
            <Header4>{t('specification.singular')}</Header4>
            {product?.specification ? (
              <Fragment>
                <span className="mr-8">{props.specification?.no}</span>
                <span> {props.specification?.name}</span>
              </Fragment>
            ) : (
              '-'
            )}
          </li>
        </DescriptionList>
        <div className="flex jc-end">
          <TertiaryButton
            onClick={props.onCancel}
            label={t('goback')}
            className="mr-32"
          />
          <PrimaryButton
            type="submit"
            label={t('save.verb')}
            disabled={isSubmitting}
            onClick={() => {
              setShowErrors(true);
              handleSubmit();
            }}
          />
        </div>
      </StickyFormHeader>
      <form onSubmit={handleSubmit}>
        <Grid container>
          <Grid item xs={12} lg={4}>
            <FormField>
              <Autocomplete
                disabled={product.id !== undefined}
                options={runSystems}
                label={t('runSystem.singular')}
                optionField="name"
                value={values.runSystem}
                onChange={runSystem => {
                  setFieldValue('runSystemId', runSystem?.id);
                  setFieldValue('gradeId', null);
                  setFieldValue('packagingId', null);
                  setFieldValue('palletId', null);
                  setFieldValue('specificationId', null);
                  setFieldValue('specification', null);
                  props.onSpecificationChange();
                }}
                onBlur={handleBlur}
                errorMessage={errors.runSystemId}
                displayError={!!touched.runSystemId || showErrors}
              />
            </FormField>
            <FormField>
              <Autocomplete
                options={grades}
                label={t('grade.singular')}
                optionField="name"
                value={values.grade}
                onChange={grade => {
                  setFieldValue('gradeId', grade?.id);
                  setFieldValue('packagingId', null);
                  setFieldValue('palletId', null);
                  setFieldValue('specificationId', null);
                  setFieldValue('specification', null);
                  props.onSpecificationChange();
                }}
                disabled={!values.runSystemId && grades.length === 0}
                onBlur={handleBlur}
                errorMessage={errors.gradeId}
                displayError={!!touched.gradeId || showErrors}
              />
            </FormField>
            <FormField>
              <Autocomplete
                options={packagings}
                label={t('packaging.singular')}
                optionField="name"
                value={values.packaging}
                onChange={packaging =>
                  setFieldValue('packagingId', packaging?.id)
                }
                onBlur={handleBlur}
              />
            </FormField>
            <FormField>
              <Autocomplete
                options={pallets}
                label={t('pallet.singular')}
                optionField="name"
                value={values.pallet}
                onChange={pallet => setFieldValue('palletId', pallet?.id)}
                onBlur={handleBlur}
              />
            </FormField>
            <InputField
              name="description"
              label={t('props.description')}
              value={values.description}
              multiline
              rows={4}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.description}
              displayError={touched.description || submitCount > 0}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <InputField
              name="runText"
              label={t('product.runText')}
              value={values.runText}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.runText}
              displayError={touched.runText || submitCount > 0}
            />

            <InputField
              name="lotText"
              label={t('product.lotText')}
              value={values.lotText}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.lotText}
              displayError={touched.lotText || submitCount > 0}
            />

            <InputField
              type="number"
              name="numberOfPallets"
              label={t('pallet.count')}
              value={values.numberOfPallets}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.numberOfPallets}
              displayError={touched.numberOfPallets || submitCount > 0}
            />

            <InputField
              name="qaStation"
              label={t('product.qaStation')}
              value={values.qaStation}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.qaStation}
              displayError={touched.qaStation || submitCount > 0}
            />

            <Grid container>
              <Grid item xs={12} md={6}>
                <InputField
                  type="number"
                  name="producedAmount"
                  label={t('product.producedAmount')}
                  value={values.producedAmount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.producedAmount}
                  displayError={touched.producedAmount || submitCount > 0}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputField
                  name="totalAmount"
                  label={t('product.totalAmount')}
                  value={values.totalAmount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.totalAmount}
                  displayError={touched.totalAmount || submitCount > 0}
                />
              </Grid>
            </Grid>
            <InputField
              name="specialMarking"
              label={t('specification.specialMarking')}
              value={
                props.specification?.specialMarking ||
                t('specification.noSpecialMarking')
              }
              disabled
              multiline
              rows={5}
            />
          </Grid>
          <Grid item lg={1} className="hide-sm">
            <Box display="flex" justifyContent="center" height="100%">
              <VerticalDivider />
            </Box>
          </Grid>
          <Grid item xs={12} lg={3}>
            <Autocomplete
              options={productStatuses}
              label={t('productStatus.singular')}
              optionField="name"
              value={values.productStatus}
              onChange={productStatus => {
                setFieldValue('productStatusId', productStatus?.id);
                setFieldValue('productStatus', productStatus);
              }}
              onBlur={handleBlur}
            />
            <hr className="my-16" />
            <SecondaryButton
              onClick={() => props.onCreateLot(product.id)}
              icon="la-plus"
              disabled={
                !product?.id ||
                product.lotNo !== null ||
                product.specificationId === null
              }
              fullWidth
              className="mb-16"
            >
              {`${t('create.verb')} ${t('product.lot')}`}
            </SecondaryButton>
            <SecondaryButton
              onClick={() => props.onReadyForMixture(product.id)}
              icon="la-blender"
              disabled={!product?.id}
              fullWidth
              className="mb-16"
            >
              {t('product.readyForMixing')}
            </SecondaryButton>
            <SecondaryButton
              onClick={() => setShowLabelsModal(true)}
              icon="la-print"
              disabled={!product?.id}
              fullWidth
              className="mb-16"
            >
              {`${t('print')} ${t('label.plural')}`}
            </SecondaryButton>

            <Tooltip
              title={isDisabled ? disabledReason : ''}
              disableHoverListener={!isDisabled}
            >
              <div style={{ display: 'block', width: '100%' }}>
                <SecondaryButton
                  onClick={() => props.onDownloadDeliveryNote()}
                  icon="la-truck"
                  disabled={isDisabled}
                  fullWidth
                  className="mb-16"
                >
                  {t('waybill.singular')}
                </SecondaryButton>
              </div>
            </Tooltip>

            <SecondaryButton
              onClick={() => props.onDownloadProductData()}
              icon="la-file-excel"
              fullWidth
            >
              {`${t('product.productdata')}`}
            </SecondaryButton>
            <Divider className="mb-16" />
            {product?.mixtures?.map(mixture => (
              <DescriptionList key={mixture.id}>
                <li>
                  <Header4>{t('mixture.singular')}</Header4>
                  <Link to={`/mixtures/${mixture.id}`}>{mixture.no}</Link>
                </li>
                <li>
                  <Header4>{t('mixture.mixDate')}</Header4>
                  {formatDate(mixture.mixDate)}
                </li>
              </DescriptionList>
            ))}
          </Grid>
        </Grid>
      </form>
      <Modal
        width={600}
        header={t('label.singular')}
        onClose={() => setShowLabelsModal(false)}
        open={showLabelsModal}
      >
        <LabelsContainer product={product} />
      </Modal>
    </Fragment>
  );
};
